import { Plus, Tags } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router';

import { mensagemDeErro } from '@/lib/erros';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type ClassificacaoPersonalizada, type EmpresaDetalhe } from '@fiscoai/contracts';

import { type DestinoRegra } from '../regras/formulario-regra';

import { entradaDaClassificacao, useClassificacoes, useSalvarClassificacao } from './api';
import { DialogoApagarClassificacao } from './dialogo-apagar-classificacao';
import { EditorClassificacao } from './editor-classificacao';
import {
  rascunhoDaClassificacao,
  rascunhoDoExemplo,
  rascunhoNovo,
  type RascunhoClassificacao,
} from './formulario-classificacao';
import { LinhaClassificacao } from './linha-classificacao';
import { EXEMPLO } from './textos';

interface Props {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}

type Abrir = (r: RascunhoClassificacao | null) => void;

function Cabecalho({ podeEditar, aoAbrir }: { podeEditar: boolean; aoAbrir: Abrir }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="grid gap-1">
        <h2 id="titulo-classificacoes" className="text-lg font-semibold">
          Classificações da empresa
        </h2>
        <p className="max-w-prose text-sm text-texto-suave">
          Quando nenhuma classificação padrão serve, crie a sua com o CFOP de entrada, o grupo
          contábil e o patrimônio que ela pede. Ela aparece no item, na nota inteira e nas regras.
        </p>
      </div>
      {podeEditar && (
        <Button
          variante="primario"
          tamanho="sm"
          onClick={() => {
            aoAbrir(rascunhoNovo());
          }}
        >
          <Plus aria-hidden /> Nova classificação
        </Button>
      )}
    </header>
  );
}

function Vazio({ podeEditar, aoAbrir }: { podeEditar: boolean; aoAbrir: Abrir }) {
  return (
    <EstadoVazio
      icone={<Tags />}
      titulo="Nenhuma classificação da empresa"
      texto={`Para o que as classificações padrão não cobrem. Ex.: “${EXEMPLO.nome}”, com entrada x${EXEMPLO.final} para os CFOPs ${EXEMPLO.cfopsFornecedor.join(', ')} do fornecedor e grupo Custo.`}
      className="py-10"
      acao={
        podeEditar && (
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variante="primario"
              onClick={() => {
                aoAbrir(rascunhoDoExemplo());
              }}
            >
              Começar por esse exemplo
            </Button>
            <Button
              onClick={() => {
                aoAbrir(rascunhoNovo());
              }}
            >
              Criar do zero
            </Button>
          </div>
        )
      }
    />
  );
}

function LinhaEditor({ children }: { children: ReactNode }) {
  return <li className="bg-superficie-hover px-4 py-4 animate-surgir">{children}</li>;
}

/** Ativar/desativar direto da lista. */
function useAlternar(empresaId: string) {
  const salvar = useSalvarClassificacao(empresaId);
  const alternar = (c: ClassificacaoPersonalizada, ativar: boolean) => {
    salvar.mutate(
      { ...entradaDaClassificacao(c), ativa: ativar, id: c.id },
      {
        onSuccess: () =>
          toast.success(
            ativar
              ? 'Classificação ativada'
              : 'Classificação desativada. Os itens já classificados continuam com ela.',
          ),
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  };
  return { alternar, alternando: salvar.isPending ? salvar.variables.id : null };
}

interface PropsLista {
  empresaId: string;
  classificacoes: ClassificacaoPersonalizada[];
  rascunho: RascunhoClassificacao | null;
  podeEditar: boolean;
  aoAbrir: Abrir;
  aoApagar: (c: ClassificacaoPersonalizada) => void;
  aoAlternar: (c: ClassificacaoPersonalizada, ativar: boolean) => void;
  alternando: string | null;
}

/** A lista; a classificação em edição (ou a nova, no fim) vira o editor no lugar. */
function Lista({ empresaId, classificacoes, rascunho, aoAbrir, ...p }: PropsLista) {
  const [, setParams] = useSearchParams();
  const editor = (r: RascunhoClassificacao) => (
    <LinhaEditor key={r.id ?? 'nova'}>
      <EditorClassificacao
        empresaId={empresaId}
        rascunho={r}
        aoFechar={() => {
          aoAbrir(null);
        }}
      />
    </LinhaEditor>
  );
  return (
    <ul
      aria-label="Classificações da empresa"
      className="divide-y divide-borda rounded-cartao border border-borda"
    >
      {classificacoes.map((c) =>
        rascunho?.id === c.id ? (
          editor(rascunho)
        ) : (
          <LinhaClassificacao
            key={c.id}
            classificacao={c}
            podeEditar={p.podeEditar}
            ocupada={p.alternando === c.id}
            aoAlternar={(ativar) => {
              p.aoAlternar(c, ativar);
            }}
            aoEditar={() => {
              aoAbrir(rascunhoDaClassificacao(c));
            }}
            aoUsarEmRegra={() => {
              const destinoRegra: DestinoRegra = { id: c.id, nome: c.nome };
              setParams({ aba: 'regras' }, { replace: true, state: { destinoRegra } });
            }}
            aoApagar={() => {
              p.aoApagar(c);
            }}
          />
        ),
      )}
      {rascunho?.id === null && editor(rascunho)}
    </ul>
  );
}

function vistaDaAba(status: 'pending' | 'error' | 'success', total: number, criando: boolean) {
  if (status === 'pending') return 'carregando';
  if (status === 'error') return 'erro';
  return total === 0 && !criando ? 'vazio' : 'lista';
}

/**
 * Classificações personalizadas da empresa (spec 0009): cada uma escrita como frase, editor no
 * lugar da linha, ativar/desativar na lista e apagar só a que ninguém usa.
 */
export function AbaClassificacoes({ empresa, podeEditar }: Props) {
  const consulta = useClassificacoes(empresa.id);
  const lista = consulta.data?.items ?? [];
  const [rascunho, setRascunho] = useState<RascunhoClassificacao | null>(null);
  const [apagando, setApagando] = useState<ClassificacaoPersonalizada | null>(null);
  const { alternar, alternando } = useAlternar(empresa.id);
  const vista = vistaDaAba(consulta.status, lista.length, rascunho?.id === null);
  return (
    <section className="grid gap-4 py-6" aria-labelledby="titulo-classificacoes">
      <Cabecalho podeEditar={podeEditar} aoAbrir={setRascunho} />
      {vista === 'carregando' && <Skeleton className="h-32 rounded-cartao" />}
      {vista === 'erro' && <p className="text-sm text-perigo">{mensagemDeErro(consulta.error)}</p>}
      {vista === 'vazio' && <Vazio podeEditar={podeEditar} aoAbrir={setRascunho} />}
      {vista === 'lista' && (
        <Lista
          empresaId={empresa.id}
          classificacoes={lista}
          rascunho={rascunho}
          podeEditar={podeEditar}
          aoAbrir={setRascunho}
          aoApagar={setApagando}
          aoAlternar={alternar}
          alternando={alternando}
        />
      )}
      <DialogoApagarClassificacao
        empresaId={empresa.id}
        classificacao={apagando}
        aoFechar={() => {
          setApagando(null);
        }}
        aoDesativar={(c) => {
          alternar(c, false);
        }}
      />
    </section>
  );
}
